import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaPlus, FaStore } from 'react-icons/fa';
import { db } from '../firebase';
import { useEffectiveUser } from '../contexts/AuthContext';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import Header from '../components/Header';
import ProfileCard from '../components/BusinessInventory/ProfileCard';
import ProfileModal from '../components/BusinessInventory/ProfileModal';
import ConfirmationModal from '../components/ConfirmationModal';

function BusinessInventory() {
  const navigate = useNavigate();
  const { currentUser } = useEffectiveUser();
  const [profiles, setProfiles] = useState([]);
  const [items, setItems] = useState({});
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch profiles and items on mount
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;

      try {
        setIsLoading(true);

        // Fetch business profiles
        const profilesRef = doc(db, 'businessProfiles', currentUser.uid);
        const profilesDoc = await getDoc(profilesRef);
        
        if (profilesDoc.exists()) {
          setProfiles(profilesDoc.data().profiles || []);
        } else {
          // Initialize empty profiles
          await setDoc(profilesRef, { 
            userId: currentUser.uid,
            profiles: [] 
          });
          setProfiles([]);
        }

        // Fetch inventory items
        const itemsRef = doc(db, 'inventoryItems', currentUser.uid);
        const itemsDoc = await getDoc(itemsRef);
        
        if (itemsDoc.exists()) {
          setItems(itemsDoc.data().items || {});
        } else {
          // Initialize empty items
          await setDoc(itemsRef, { 
            userId: currentUser.uid,
            items: {} 
          });
          setItems({});
        }
      } catch (error) {
        console.error('Error fetching business inventory data:', error);
        toast.error('Failed to load business inventory');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  const getItemCount = (profileId) => {
    return Object.values(items).filter(item => item.profileId === profileId).length;
  };

  const handleProfileSubmit = async (formData) => {
    try {
      const profilesRef = doc(db, 'businessProfiles', currentUser.uid);
      
      if (editingProfile) {
        // Update existing profile
        const updatedProfiles = profiles.map(p => 
          p.id === editingProfile.id 
            ? { ...p, ...formData, updatedAt: new Date() }
            : p
        );
        
        await updateDoc(profilesRef, { profiles: updatedProfiles });
        setProfiles(updatedProfiles);
        toast.success('Business profile updated successfully');
      } else {
        // Create new profile
        const newProfile = {
          id: `profile_${Date.now()}`,
          ...formData,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        const updatedProfiles = [...profiles, newProfile];
        await updateDoc(profilesRef, { profiles: updatedProfiles });
        setProfiles(updatedProfiles);
        toast.success('Business profile created successfully');
      }
      
      setShowProfileModal(false);
      setEditingProfile(null);
    } catch (error) {
      console.error('Error saving business profile:', error);
      toast.error('Failed to save business profile');
    }
  };

  const handleEditProfile = (profile) => {
    setEditingProfile(profile);
    setShowProfileModal(true);
  };

  const handleDeleteProfile = (profile) => {
    setProfileToDelete(profile);
    setDeleteModalOpen(true);
  };

  const confirmDeleteProfile = async () => {
    if (!profileToDelete) return;

    try {
      // Delete the profile
      const updatedProfiles = profiles.filter(p => p.id !== profileToDelete.id);
      const profilesRef = doc(db, 'businessProfiles', currentUser.uid);
      await updateDoc(profilesRef, { profiles: updatedProfiles });
      setProfiles(updatedProfiles);

      // Delete all items belonging to this profile
      const itemsToKeep = Object.fromEntries(
        Object.entries(items).filter(([_, item]) => item.profileId !== profileToDelete.id)
      );
      
      if (Object.keys(items).length !== Object.keys(itemsToKeep).length) {
        const itemsRef = doc(db, 'inventoryItems', currentUser.uid);
        await updateDoc(itemsRef, { items: itemsToKeep });
        setItems(itemsToKeep);
      }

      toast.success('Business profile deleted successfully');
      setDeleteModalOpen(false);
      setProfileToDelete(null);
    } catch (error) {
      console.error('Error deleting business profile:', error);
      toast.error('Failed to delete business profile');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-violet-50/50 dark:bg-gray-700">
        <Header />
        <div className="container mx-auto py-5 px-4 max-w-6xl">
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-600 dark:text-gray-400">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-violet-50/50 dark:bg-gray-700 select-none">
      <Header />
      
      <div className="container mx-auto py-5 px-4 max-w-6xl">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-2xl md:text-4xl min-h-12 font-bold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">
            Business Inventory
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Track your business products and inventory
          </p>
        </motion.div>

        {/* Add Profile Button - Centered */}
        <div className="flex justify-center mb-8">
          <button
            onClick={() => {
              setEditingProfile(null);
              setShowProfileModal(true);
            }}
            className="flex items-center px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 
              text-white rounded-lg hover:shadow-lg transition-all whitespace-nowrap"
          >
            <FaPlus className="mr-2" />
            Add Business Profile
          </button>
        </div>

        {/* Empty State */}
        {profiles.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20 bg-white dark:bg-gray-800 rounded-xl shadow-lg"
          >
            <FaStore className="mx-auto h-16 w-16 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
              No business profiles yet
            </h3>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Get started by creating your first business profile
            </p>
            <button
              onClick={() => setShowProfileModal(true)}
              className="mt-6 inline-flex items-center px-6 py-3 bg-gradient-to-r from-teal-500 
                to-cyan-600 text-white rounded-lg hover:shadow-lg transition-all"
            >
              <FaPlus className="mr-2" />
              Create Your First Profile
            </button>
          </motion.div>
        ) : (
          /* Profile Cards Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {profiles.map((profile, index) => (
              <ProfileCard
                key={profile.id}
                profile={profile}
                itemCount={getItemCount(profile.id)}
                onView={() => navigate(`/business-inventory/${profile.id}`)}
                onEdit={() => handleEditProfile(profile)}
                onDelete={() => handleDeleteProfile(profile)}
                index={index}
              />
            ))}
          </div>
        )}
      </div>

      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => {
          setShowProfileModal(false);
          setEditingProfile(null);
        }}
        onSubmit={handleProfileSubmit}
        editingProfile={editingProfile}
      />

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setProfileToDelete(null);
        }}
        onConfirm={confirmDeleteProfile}
        title="Delete Business Profile"
        message={`Are you sure you want to delete "${profileToDelete?.name}"? All items in this profile will also be deleted.`}
      />
    </div>
  );
}

export default BusinessInventory;

